/** The quick actions every value in the External access drawer offers.
 *
 *  An access-log row names a key, a partner, a user and a tenant; each opens its own page
 *  and offers "the calls of this …" on /external-access (always with a created window —
 *  the grid refuses a windowless search). Records that no longer exist (the row outlives
 *  them) get no "open" link, only the filter by id. Every href comes from gridLinks; every
 *  action from ValueActions' `act`.
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { cond, externalAccessHref, idCond } from '@/utils/gridLinks';

export function credentialActions(
  credentialId: number,
  credential: { id: number; key_prefix: string } | null,
): ValueAction[] {
  const out: ValueAction[] = [];
  if (credential) out.push(act.open(`/api-credentials/${credential.id}`, 'Open API credential'));
  out.push(act.filter(externalAccessHref([cond('credential', String(credentialId), 'exact')]), 'Calls with this key'));
  if (credential?.key_prefix) out.push(act.copy(credential.key_prefix, 'Copy key prefix'));
  return out;
}

export function partnerActions(partnerId: number, partner: { id: number } | null): ValueAction[] {
  const out: ValueAction[] = [];
  if (partner) out.push(act.open(`/partners/${partner.id}`, 'Open partner'));
  out.push(act.filter(externalAccessHref([idCond('partner', partnerId)]), 'Calls of this partner'));
  return out;
}

export function userActions(userId: number, user: { id: number; email: string } | null): ValueAction[] {
  const out: ValueAction[] = [];
  if (user) out.push(act.open(`/users/${user.id}`, 'Open user'));
  out.push(act.filter(externalAccessHref([cond('user', String(userId), 'exact')]), 'Calls of this user'));
  if (user) out.push(act.mailto(user.email));
  return out;
}

export function tenantActions(tenantId: number, tenant: { id: number } | null): ValueAction[] {
  const out: ValueAction[] = [];
  if (tenant) out.push(act.open(`/tour-operators/${tenant.id}`, 'Open tour operator'));
  out.push(act.filter(externalAccessHref([], { tenant: tenantId }), 'Calls on this tenant'));
  return out;
}

export function requestIdActions(requestId: string): ValueAction[] {
  return [
    act.copy(requestId, 'Copy request ID'),
    act.filter(externalAccessHref([cond('request', requestId, 'exact')]), 'Calls with this request ID'),
  ];
}

export function ipActions(ip: string): ValueAction[] {
  return [
    act.whois(ip),
    act.filter(externalAccessHref([cond('ip', ip, 'exact')]), 'Calls from this IP'),
    act.copy(ip, 'Copy IP'),
  ];
}

/** The ±5m … ±7d windows around the call, on the External access grid. */
export function timeActions(iso: string): ValueAction[] {
  return [act.copy(iso, 'Copy timestamp'), ...act.timeRanges(iso, (range) => externalAccessHref([range]))];
}

/** The stored webhook / API request the call wrote (its body lives there, never here). */
export function apiRequestActions(id: number): ValueAction[] {
  return [act.open(`/api-requests/${id}/info`, 'Open API request'), act.copy(String(id), 'Copy API request id')];
}

/** The import run the call started. */
export function importRunActions(id: string): ValueAction[] {
  return [act.open(`/import-runs/${id}`, 'Open import run'), act.copy(id, 'Copy import run id')];
}
